import "./index.css";
import { Check, Minus } from "lucide-react";
import type { ChangeEvent, MouseEvent, ReactNode } from "react";
import { useId } from "react";
import type { ICheckboxProps } from "./types";

export const Checkbox = (props: ICheckboxProps) => {
  const {
    checked,
    onChange,
    label,
    description,
    accessory,
    checkboxPosition = "start",
    disabled = false,
    className,
    id,
    style,
    isCard = false,
    indeterminate = false,
    size = "md",
    onClick,
    renderCheckbox,
    ...inputProps
  } = props;

  const fallbackId = useId();
  const checkboxId = id || fallbackId;

  const iconSize = size === "sm" ? 14 : 16;

  const rootClassName = [
    "nuco-checkbox",
    "nuco-color-brand",
    checkboxPosition === "end" && "nuco-checkbox--reverse",
    description && "nuco-checkbox--with-description",
    disabled && "nuco-checkbox--disabled",
    isCard && "nuco-checkbox--card",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const boxClassName = [
    "nuco-checkbox__box",
    `nuco-checkbox__box--${size}`,
    checked && "nuco-checkbox__box--checked",
    indeterminate && "nuco-checkbox__box--indeterminate",
  ]
    .filter(Boolean)
    .join(" ");

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.checked, e);
  };

  const handleLabelClick = (e: MouseEvent<HTMLLabelElement>) => {
    if (onClick) {
      onClick(e as unknown as MouseEvent<HTMLInputElement>);
    }
  };

  const descriptionId = description ? `${checkboxId}__desc` : undefined;
  const accessoryId = accessory ? `${checkboxId}-accessory` : undefined;

  let content: ReactNode;
  if (renderCheckbox) {
    const { renderCheckbox: _, ...restProps } = props;
    content = renderCheckbox({ ...restProps, checkboxId });
  } else {
    content = (label || description) && (
      <div className="nuco-checkbox__content">
        {label && (
          <div className="nuco-checkbox__label-wrapper">
            <span className="nuco-checkbox__label nuco-text nuco-text--md">
              {label}
            </span>
            {accessory && (
              <div
                id={accessoryId}
                className="nuco-checkbox__accessory"
                role="group"
              >
                {accessory}
              </div>
            )}
          </div>
        )}
        {description && (
          <div
            className="nuco-checkbox__description nuco-text nuco-text--md"
            id={descriptionId}
          >
            {description}
          </div>
        )}
      </div>
    );
  }

  return (
    <label
      className={rootClassName}
      htmlFor={checkboxId}
      data-iscard={isCard}
      style={style}
      onClick={handleLabelClick}
    >
      <input
        {...inputProps}
        id={checkboxId}
        type="checkbox"
        className="nuco-checkbox__input"
        tabIndex={disabled ? -1 : 0}
        checked={checked}
        onChange={handleChange}
        disabled={disabled}
        aria-label={
          !label && typeof inputProps["aria-label"] === "string"
            ? inputProps["aria-label"]
            : undefined
        }
        aria-describedby={
          [descriptionId, accessoryId].filter(Boolean).join(" ") || undefined
        }
      />

      {!renderCheckbox && (
        <div className={boxClassName}>
          <div className="nuco-checkbox__icon">
            {indeterminate ? (
              <Minus size={iconSize} />
            ) : (
              <Check size={iconSize} />
            )}
          </div>
        </div>
      )}

      {content}
    </label>
  );
};
